// The two sentences every reach row carries, said once above the rows rather
// than in each of them (#1461, ADR-0269 decision 6), and the one every
// untagged-upstream row carries (decision 4d), said once above the rows where
// there is an untagged row to be true of (#1482).
//
// **The API is unchanged, and that is the constraint this file exists under.**
// A reach row's `detail` still ends on the RADIUS blind spot, an agreeing row
// still says the documents agreeing is not a reading of what the port runs,
// an untagged row's still ends its own sentence on decision 4d, and the row's
// held plane still carries the blind spot as `not_checked` and 4d as
// `untagged_vlan_id`:
// a plan's acceptance line is built by the same producer
// (`internal/portreach`), so changing the words there would change what an
// operator accepts. So the SPA removes them **where it shows a row** and
// nowhere else, matching the producer's exact text. `reachCaveats.test.ts`
// reads `internal/portreach/portreach.go` and fails if any of the strings
// stops being the producer's, which would otherwise show each sentence twice
// rather than fail.
//
// The plan lines (`networks/PortReachLines.tsx`) are not shown through this:
// an acceptance line is read once, at an apply, and stays whole.
//
// **On an `mpsk` network's rows the blind spot is two other sentences**
// (ADR-0305 decision 6, `6a`): a VLAN a RADIUS server other than Zenith
// assigns is not checked, and a row carrying PAN VLANs says the router is not
// read. They are said once above the rows too, each only where a row shown
// carries it, so the general sentence is never said over rows it is not true of.
import type { components } from "../api/schema";

type Row = components["schemas"]["ComparisonRow"];

/** `portreach.RadiusBlindSpot`, byte for byte. */
export const RADIUS_BLIND_SPOT = "VLANs RADIUS assigns per client are not in either configuration and are not checked.";

/** `portreach.RadiusBlindSpotMPSK`, byte for byte: the blind spot on an `mpsk`
 * network's rows, whose PAN VLANs are judged (ADR-0305 `6a`). */
export const RADIUS_BLIND_SPOT_MPSK = "VLANs a RADIUS server other than Zenith assigns are not checked.";

/** `portreach.RouterNotRead`, byte for byte: said on every row carrying a PAN
 * VLAN (ADR-0305 `6a`). */
export const ROUTER_NOT_READ = "Whether the router, or anything upstream of this switch, carries these VLANs is not read.";

/** The blind-spot sentences, in the order they are said above the rows. */
const BLIND_SPOTS = [RADIUS_BLIND_SPOT, ROUTER_NOT_READ, RADIUS_BLIND_SPOT_MPSK] as const;

/** The blind-spot sentences the given rows carry, each once, in BLIND_SPOTS'
 * order. With no row to read them from it is ADR-0269's own sentence, which is
 * what every row said before ADR-0305. */
export function blindSpotsOf(rows: readonly Row[]): string[] {
  const said = BLIND_SPOTS.filter((s) => rows.some((r) => (r.detail ?? "").includes(s)));
  return said.length > 0 ? said : [RADIUS_BLIND_SPOT];
}

/** The clause `portreach.Sentence` ends an agreeing check on, after "the
 * documents agree". Only the clause is removed, so a row still says the
 * documents agree — and a row whose switch's report then disagrees still
 * reads "the documents agree. But …". */
export const NOT_A_READING = ", which is not a reading of what the port runs";

/** `portreach.UntaggedIDNotCheckable`, byte for byte: decision 4d, which every
 * untagged-upstream row's `detail` and held `untagged_vlan_id` carry. */
export const UNTAGGED_ID_NOT_CHECKABLE = "An access point's untagged interface names no VLAN id, so whether it is the same VLAN as the port's untagged one is not checkable.";

/** Decision 4d in the words said above the rows: the producer's sentence,
 * prefixed with the name an untagged row goes by (`reachNeed`), so that it reads
 * as about those rows and not about every row. */
export const UNTAGGED_CAVEAT = `Untagged upstream: ${UNTAGGED_ID_NOT_CHECKABLE}`;

/** The declared management VLAN in the words said above the rows (ADR-0313
 * ruling 3, ADR-0327; #1592): what an untagged row is compared with where the
 * site declares one, which 4d's sentence does not say on its own. */
export const MANAGEMENT_CAVEAT = "Where this site declares a management VLAN, each access point's untagged upstream row also compares the port's untagged VLAN in the configuration the controller holds for the switch with that declaration.";

/** The site's declared management VLAN as the rows carry it, or undefined
 * where no row does — a site that declares none is compared on nothing. */
export const declaredManagementVLAN = (rows: readonly Row[]): string | undefined =>
  rows.find((r) => r.facet === "reach" && r.held?.fields.management_vlan !== undefined)?.held?.fields.management_vlan;

/** Whether a reach row is an access point's untagged upstream, the only rows
 * decision 4d is true of. */
export const isUntagged = (row: Row): boolean => row.facet === "reach" && row.element.endsWith("/untagged");

/** The held-not-running caveat in the words said above the rows. */
export const HELD_NOT_RUNNING = "Where a row agrees, it is the two configurations the controller holds that agree, which is not a reading of what the port runs.";

/** A row's `detail` as shown under the caveats: the sentences said above the
 * rows removed, nothing else touched. */
export function reachDetail(detail: string | undefined): string | undefined {
  if (detail === undefined) return undefined;
  let shown = detail.split(NOT_A_READING).join("");
  for (const sentence of [...BLIND_SPOTS, UNTAGGED_ID_NOT_CHECKABLE]) shown = shown.split(" " + sentence).join("").split(sentence).join("");
  return shown.trim();
}

/** The held fields that repeat a caveat said above the rows, each dropped only
 * while its value is exactly that caveat. */
const REPEATS: readonly (readonly [string, string])[] = [
  ["not_checked", RADIUS_BLIND_SPOT], ["not_checked", RADIUS_BLIND_SPOT_MPSK], ["not_read", ROUTER_NOT_READ],
  ["untagged_vlan_id", UNTAGGED_ID_NOT_CHECKABLE],
];

/** A reach row as shown under the caveats: its `detail` without them, and its
 * held plane without the `not_checked` and `untagged_vlan_id` fields that
 * repeat them. Every other field, and either of those with any other value, is
 * the server's as it came. */
export function reachShown(row: Row): Row {
  if (row.facet !== "reach") return row;
  let held = row.held;
  if (held && REPEATS.some(([k, v]) => held!.fields[k] === v)) {
    const fields = { ...held.fields };
    for (const [k, v] of REPEATS) if (fields[k] === v) delete fields[k];
    held = { ...held, fields };
  }
  return { ...row, detail: reachDetail(row.detail), held };
}

/** Whether a reach row is an `mpsk` network that no access point serves
 * (ADR-0305 `5b`): no access point to name, only the network. */
export const isUnserved = (row: Row): boolean => row.facet === "reach" && row.element.startsWith("network/");

/** Whether a reach row is a switch's own uplink (ADR-0310 `7a`): about the
 * switch, against what the access points on it need. */
export const isUplink = (row: Row): boolean => row.facet === "reach" && row.element.endsWith("/uplink");

/** Which of an access point's needs a reach row is about, or on an uplink row,
 * that it is the switch's uplink. */
export function reachNeed(row: Row): string | undefined {
  if (isUnserved(row)) return "PAN VLANs, not checked";
  if (isUplink(row)) return `uplink ${row.held?.fields.uplink ?? ""}`.trim();
  if (row.element.includes("/pans/")) return `PAN VLANs of ${row.held?.fields.network ?? "an mpsk network"}`;
  if (row.element.endsWith("/untagged")) return "untagged upstream";
  if (row.element.endsWith("/port")) return "the port";
  return row.refs.vlan !== undefined ? `VLAN ${row.refs.vlan}` : undefined;
}

/** The caveats, once, above the rows they are true of: the two every row is
 * subject to, and decision 4d only where `rows` holds an untagged upstream. */
export function ReachCaveats({ rows = [] }: { rows?: readonly Row[] }) {
  return (
    <>
      <p className="muted small" data-reach-caveats>
        {/* Restates ADR-0269 decisions 2a and 6, and ADR-0305 decision 6 — both sides are documents
            the cell holds, never what the port runs, so an agreement is between documents; a VLAN
            RADIUS assigns per client is in neither, so it is not checked, except an mpsk network's
            PAN VLANs, which are judged, so on those rows what stays blind is a VLAN another RADIUS
            server assigns, and the router. Each sentence is said once, where a row below carries
            it, and no row repeats it (#1461, #1560). */}
        {HELD_NOT_RUNNING} {blindSpotsOf(rows).join(" ")}
      </p>
      {rows.some(isUntagged) && (
        <p className="muted small" data-reach-untagged>
          {/* Restates ADR-0269 decision 4d — an access point's untagged interface names no VLAN
              id, so whether it is the port's untagged VLAN is not checkable. True of the untagged
              upstream rows alone, so it is said only where one is shown, and no untagged row
              repeats it in its sentence or its held plane (#1482). */}
          {UNTAGGED_CAVEAT}
        </p>
      )}
      {declaredManagementVLAN(rows) !== undefined && (
        <p className="muted small" data-reach-management>
          {/* Restates ADR-0313 ruling 3 as ADR-0327 transcribes its menus — a site may declare one
              management VLAN, only access-point ports are compared with it, on the untagged row, and
              a difference reads differs; a site that declares none is compared on nothing, so this is
              said only where a row carries the declaration (#1592). */}
          {MANAGEMENT_CAVEAT} This site declares VLAN {declaredManagementVLAN(rows)}.
        </p>
      )}
    </>
  );
}
